import React, { useEffect, useState, useMemo } from 'react'
import { DialogContent, MenuItem, Checkbox, InputAdornment } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import StyledDialog from '../../Common/Dialogs/StyledDialog'
import StyledTextField from '../../Common/StyledTextField'
import StyledMultiSelect from '../../Common/StyledMultiSelect'
import StyledCurrencyTextField from '../../Common/StyledCurrencyTextField'
import StyledAlert from '../../Common/StyledAlert'
import { MainButton } from '../../Button/MainButton'
import {
	DIALOG_EDIT_MODES,
	TRAINING_COURSE_TEMPLATE,
	TRAINING_SESSION_LENGTHS,
	TRAINING_AGE_LABELS,
	onlyNumbers,
	capitilizeMultipleWords,
	TRAINING_AGE_MONTH_REQUIREMENTS,
	TRAINING_SESSION_TEMPLATE,
	TRAINING_MATERIAL_TEMPLATE,
	NUMBER_OF_SESSIONS,
	NON_BREAKING_SPACE,
	TRAINING_COURSE_CATEGORY,
	TRAINING_COURSE_ABBREVIATIONS
} from '../../../utils'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import useConstants from '../../../hooks/useConstants'

const useStyles = makeStyles(() => ({
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		padding: '0 1.5rem',
		gap: '1rem',
		flex: 1
	},
	rowHeader: {
		fontSize: '1rem'
	},
	rowWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1.5rem'
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		'& > div': {
			flex: 1
		},
		gap: '1.5rem'
	},
	dialogActions: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: '1rem',
		margin: '2rem 0 1rem 0',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		},
		justifySelf: 'flex-end'
	},
	abbrAdornment: {
		marginLeft: '10px',
		marginRight: 0,
		marginTop: '19px !important',
		'& > p': {
			fontSize: '1rem'
		}
	},
	alertContainer: {
		display: 'flex'
	}
}))

const SETUP_STEPS = {
	DETAILS: 'details',
	SESSIONS: 'sessions'
}

/**
 *
 * @param {{
  open: Boolean, 
	onClose: Function, 
	onCancelled: Function,
	title: String,
	mode: "new" | "edit", 
	course: TRAINING_COURSE_TEMPLATE
}} params
 * @returns
 */
const TrainingCourseDialog = ({
	open,
	onSaved,
	onCancelled,
	title = 'Area',
	mode = DIALOG_EDIT_MODES.New,
	course = {}
}) => {
	const classes = useStyles()

	const [editedCourse, setEditedCourse] = useState({ ...TRAINING_COURSE_TEMPLATE })
	const [editedKeys, setEditedKeys] = useState(new Set())
	const [setupStep, setSetupStep] = useState(SETUP_STEPS.DETAILS)

	const { loading, facility, saveTrainingCourseForFacility, updateTrainingCourseForFacility } =
		useSelectedFacility()
	const { trainingCourseIdentifiers, trainingSkills, trainingSizes, requiredVaccines } = useConstants()

	useEffect(() => {
		if (course && Object.keys(course).length > 0) {
			const { age_min_months = null, age_max_months = null } = course

			const age_category = Object.entries(TRAINING_AGE_MONTH_REQUIREMENTS).find(([category, [min, max]]) => {
				return min === age_min_months && max === age_max_months
			})

			setEditedCourse({ ...course, age_category: age_category ? age_category[0] : '' })
		}
	}, [course, setEditedCourse])

	const classesExistForCourse = useMemo(() => {
		const { number_of_classes } = editedCourse
		return number_of_classes > 0
	}, [editedCourse])

	const handleResetDialog = () => {
		setEditedCourse({ ...TRAINING_COURSE_TEMPLATE })
		setSetupStep(SETUP_STEPS.DETAILS)
		setEditedKeys(new Set())
	}

	const handleOnCancelled = () => {
		if (onCancelled) {
			handleResetDialog()
			onCancelled()
		}
	}

	const handleOnSave = async () => {
		if (mode === DIALOG_EDIT_MODES.New) {
			const payload = {
				...editedCourse,
				course_id_prefix: courseIdPrefix,
				course_id_suffix:
					editedCourse.course_id_suffix.length > 0 ? `-${editedCourse.course_id_suffix}` : ''
			}

			await saveTrainingCourseForFacility({
				course: payload,
				facility_id: facility._id
			})
		}
		if (mode === DIALOG_EDIT_MODES.Edit && editedKeys.size > 0) {
			const updates = {}
			for (const key of editedKeys) {
				updates[key] = editedCourse[key]
			}

			const { _id } = editedCourse
			await updateTrainingCourseForFacility({ course_id: _id, updates, facility_id: facility._id })
			setEditedKeys(new Set())
		}
		if (onSaved) {
			handleResetDialog()
			onSaved()
		}
	}

	const handleFieldUpdate = ({ field, type = 'string', targetValue }) => {
		const [mainField, subField] = field.split('.')

		setEditedCourse((prev) => {
			const revised = { ...prev }
			let value = targetValue

			if (type === 'number') {
				value = onlyNumbers(targetValue)
			}

			if (subField) {
				revised[mainField] = { ...revised[mainField], [subField]: value }
			} else {
				revised[field] = value
			}

			if (field === 'age_category') {
				const [min, max] = TRAINING_AGE_MONTH_REQUIREMENTS[value]
				revised.age_min_months = min
				revised.age_max_months = max
			}

			if (field === 'name') {
				const { _id } = trainingCourseIdentifiers.find((course) => course.name === value)
				revised.training_course_identifier_id = _id
			}

			if (field === 'course_id_suffix') {
				// remove any non-alphanumeric characters
				value = value.replace(/[^a-zA-Z0-9\-]/g, '')
				revised.course_id_suffix = value.toUpperCase()
			}

			if (field === 'number_of_sessions') {
				const { sessions, training_course_identifier_id } = editedCourse
				const newSessions = []
				if (sessions.length === 0 && mode === DIALOG_EDIT_MODES.New) {
					const { abbreviation } = trainingCourseIdentifiers.find(
						(course) => course._id === training_course_identifier_id
					)
					let setAllToNone = false
					if (
						abbreviation &&
						[TRAINING_COURSE_ABBREVIATIONS.PVT, TRAINING_COURSE_ABBREVIATIONS.FVRTS].includes(
							abbreviation
						)
					) {
						setAllToNone = true
					}

					for (let i = 0; i < value; i++) {
						newSessions.push({
							...TRAINING_SESSION_TEMPLATE,
							overview: setAllToNone ? 'none' : TRAINING_SESSION_TEMPLATE.overview,
							session: i + 1,
							training_materials: [
								{
									...TRAINING_MATERIAL_TEMPLATE,
									file_path: setAllToNone ? 'none' : TRAINING_MATERIAL_TEMPLATE.file_path,
									description: setAllToNone ? 'none' : TRAINING_MATERIAL_TEMPLATE.description
								}
							]
						})
					}
					revised.sessions = newSessions
				} else if (sessions.length > 0) {
					for (let i = 0; i < value; i++) {
						if (mode === DIALOG_EDIT_MODES.New) {
							if (sessions[i]) {
								newSessions.push(sessions[i])
							} else {
								newSessions.push({
									...TRAINING_SESSION_TEMPLATE,
									session: i + 1,
									training_materials: [{ ...TRAINING_MATERIAL_TEMPLATE }]
								})
							}
						}
						if (mode === DIALOG_EDIT_MODES.Edit) {
							if (sessions[i]) {
								newSessions.push(sessions[i])
							} else if (course.sessions[i]) {
								newSessions.push(course.sessions[i])
							} else {
								newSessions.push({
									...TRAINING_SESSION_TEMPLATE,
									session: i + 1,
									training_materials: [{ ...TRAINING_MATERIAL_TEMPLATE }]
								})
							}
						}
					}
					revised.sessions = newSessions
				}
			}

			return revised
		})
		setEditedKeys((keys) => keys.add(mainField))
	}

	const courseIdPrefix = useMemo(() => {
		if (!facility || !editedCourse || !trainingCourseIdentifiers) {
			return ''
		}

		const { abbreviation: facilityAbbr } = facility || ''
		const name = editedCourse.name || editedCourse.training_course_identifier_id?.name
		if (!name) {
			return ''
		}
		const courseIdInfo = trainingCourseIdentifiers.find((course) => course.name === name)
		return `${courseIdInfo?.abbreviation}-${facilityAbbr}`
	}, [facility, trainingCourseIdentifiers, editedCourse])

	const canSave = useMemo(() => {
		const { sessions } = editedCourse
		const pathErrors = sessions.some((session) =>
			session.training_materials.some((m) => m.path_error > '' || m.file_path === '')
		)
		const missingSessionOverview = sessions.some((session) => session.overview === '')
		const missingHomeworkOverview = sessions.some((session) =>
			session.training_materials.some((m) => m.description === '')
		)

		return (
			editedCourse.name > '' &&
			editedCourse.short_name > '' &&
			// editedCourse.course_id_suffix > '' &&
			editedCourse.capacity_max > 0 &&
			editedCourse.number_of_sessions > 0 &&
			editedCourse.session_length > 0 &&
			editedCourse.description > '' &&
			editedCourse.short_description > '' &&
			editedCourse.target_audience_information > '' &&
			// editedCourse.size_restrictions.length > 0 &&
			editedCourse.required_vaccines.length > 0 &&
			editedCourse.price_per_session > 0 &&
			!pathErrors &&
			!missingSessionOverview &&
			!missingHomeworkOverview
		)
	}, [editedCourse])

	return (
		<>
			<StyledDialog
				showBackButton={setupStep === SETUP_STEPS.SESSIONS}
				onBackButtonPressed={() => setSetupStep(SETUP_STEPS.DETAILS)}
				open={open}
				onClose={onCancelled}
				title={title}
				showActivityIndicator={loading}>
				<DialogContent style={{}}>
					{classesExistForCourse ? (
						<div className={classes.dialogContent}>
							<StyledAlert
								type={'info'}
								text="Classes exist for this course. Some fields are no longer editable"
							/>
						</div>
					) : null}
					<div className={classes.dialogContent}>
						{setupStep === SETUP_STEPS.DETAILS ? (
							<>
								<div>
									<h3 className={classes.rowHeader}>Details</h3>
									<div className={classes.rowWrapper}>
										<div className={classes.row}>
											<div>
												<StyledTextField
													variant="filled"
													label="Name"
													select
													required
													value={
														editedCourse.name ||
														editedCourse.training_course_identifier_id?.name
													}
													disabled={
														mode === DIALOG_EDIT_MODES.Edit ||
														mode === DIALOG_EDIT_MODES.ViewOnly
													}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'name',
															type: 'string',
															targetValue: e.target.value
														})
													}>
													{trainingCourseIdentifiers.map(({ name, abbreviation }) => {
														return (
															<MenuItem key={abbreviation} value={name}>
																{name}
															</MenuItem>
														)
													})}
												</StyledTextField>
											</div>
											<div>
												<StyledTextField
													variant="filled"
													label="Name Abbreviation (max 10 characters)"
													disabled={
														mode === DIALOG_EDIT_MODES.ViewOnly ||
														classesExistForCourse
													}
													value={editedCourse.short_name}
													inputProps={{ maxLength: 10 }}
													required
													onChange={(e) =>
														handleFieldUpdate({
															field: 'short_name',
															type: 'string',
															targetValue: e.target.value
														})
													}
												/>
											</div>
										</div>
										<div className={classes.row}>
											<div>
												<StyledTextField
													variant="filled"
													label="Course ID (Max 9 extra characters)"
													// required
													value={
														mode === DIALOG_EDIT_MODES.New
															? editedCourse.course_id_suffix
															: editedCourse.course_id
													}
													inputProps={{ maxLength: 9 }}
													disabled={
														mode === DIALOG_EDIT_MODES.ViewOnly ||
														mode === DIALOG_EDIT_MODES.Edit ||
														editedCourse.name === ''
													}
													onChange={(e) => {
														if (mode === DIALOG_EDIT_MODES.New) {
															handleFieldUpdate({
																field: 'course_id_suffix',
																type: 'string',
																targetValue: e.target.value
															})
														}
													}}
													InputProps={{
														startAdornment:
															mode === DIALOG_EDIT_MODES.New ? (
																<InputAdornment
																	className={classes.abbrAdornment}
																	position="start">
																	{`${courseIdPrefix}${
																		editedCourse.course_id_suffix.length > 0
																			? '-'
																			: ''
																	}`}
																</InputAdornment>
															) : null
													}}
												/>
											</div>
											<div>
												<StyledTextField
													variant="filled"
													label="Number of Sessions"
													select
													required
													value={editedCourse.number_of_sessions}
													disabled={
														mode === DIALOG_EDIT_MODES.ViewOnly ||
														classesExistForCourse
													}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'number_of_sessions',
															type: 'number',
															targetValue: e.target.value
														})
													}>
													{NUMBER_OF_SESSIONS.map((number) => {
														return (
															<MenuItem
																key={`session_number-${number}`}
																value={number}>
																{number}
															</MenuItem>
														)
													})}
												</StyledTextField>
											</div>
										</div>
										<div className={classes.row}>
											<div>
												<StyledTextField
													variant="filled"
													label="Length of Sessions"
													select
													required
													value={editedCourse.session_length}
													disabled={
														mode === DIALOG_EDIT_MODES.ViewOnly ||
														classesExistForCourse
													}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'session_length',
															type: 'number',
															targetValue: e.target.value
														})
													}>
													{Object.entries(TRAINING_SESSION_LENGTHS).map(
														([number, label]) => {
															return (
																<MenuItem
																	key={`session_number-${number}`}
																	value={number}>
																	{label}
																</MenuItem>
															)
														}
													)}
												</StyledTextField>
											</div>
											<div>
												<StyledTextField
													variant="filled"
													label="Capacity Maximum"
													required
													value={editedCourse.capacity_max}
													disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'capacity_max',
															type: 'number',
															targetValue: e.target.value
														})
													}
												/>
											</div>
										</div>
										<div className={classes.row}>
											<div>
												<StyledTextField
													variant="filled"
													label="Course Description (Short)"
													multiline
													charcounter
													required
													value={editedCourse.short_description}
													disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
													inputProps={{ maxLength: 100, minRows: 3 }}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'short_description',
															type: 'string',
															targetValue: e.target.value
														})
													}
													header="Short Description"
													tooltip={{
														tooltipText:
															'1 sentence description to preview the course in the client app.'
													}}
												/>
											</div>
										</div>
										<div className={classes.row}>
											<div>
												<StyledTextField
													variant="filled"
													label="Course Description (Long)"
													multiline
													charcounter
													required
													value={editedCourse.description}
													disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
													inputProps={{ maxLength: 500, minRows: 3 }}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'description',
															type: 'string',
															targetValue: e.target.value
														})
													}
													header="Long Description"
													tooltip={{
														tooltipText:
															'Lengthier description (3-4 sentences) to provide more information on the course in the client app.'
													}}
												/>
											</div>
										</div>
										<div className={classes.row}>
											<div>
												<StyledTextField
													variant="filled"
													label="This course is right for you if..."
													multiline
													charcounter
													required
													value={editedCourse.target_audience_information}
													disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
													inputProps={{ maxLength: 500, minRows: 3 }}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'target_audience_information',
															type: 'string',
															targetValue: e.target.value
														})
													}
												/>
											</div>
										</div>
									</div>
								</div>
								<div>
									<h3 className={classes.rowHeader}>Eligibility Requirements</h3>
									<div className={classes.rowWrapper}>
										<div className={classes.row}>
											<div>
												<StyledMultiSelect
													label="Prerequisites"
													disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
													values={editedCourse.prerequisite_skills}
													onValuesChanged={(prereqs) => {
														handleFieldUpdate({
															field: 'prerequisite_skills',
															targetValue: prereqs
														})
													}}
													useChips={true}
													chipLabelRenderFunction={(value) => {
														const skill = trainingSkills.find((s) => s._id === value)
														return capitilizeMultipleWords(skill?.name || 'unknown')
													}}
													menuItems={trainingSkills.map(({ _id, name }) => {
														return (
															<MenuItem key={_id} value={_id}>
																<Checkbox
																	checked={
																		editedCourse.prerequisite_skills.indexOf(
																			_id
																		) > -1
																	}
																/>
																{name}
															</MenuItem>
														)
													})}
												/>
											</div>
											<div>
												<StyledMultiSelect
													singleSelect
													label="Age"
													required
													disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
													values={editedCourse.age_category}
													onValuesChanged={(category) => {
														handleFieldUpdate({
															field: 'age_category',
															targetValue: category
														})
													}}
													useChips={true}
													chipLabelRenderFunction={(value = '') => {
														return capitilizeMultipleWords(value, '_')
													}}
													menuItems={Object.entries(TRAINING_AGE_LABELS).map(
														([value, label]) => {
															return (
																<MenuItem
																	key={`age-category-${value}`}
																	value={value}>
																	<Checkbox
																		checked={
																			editedCourse.age_category === value
																		}
																	/>
																	{label}
																</MenuItem>
															)
														}
													)}
												/>
											</div>
										</div>
										<div className={classes.row}>
											<div>
												<StyledMultiSelect
													label="Sizes"
													disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
													values={editedCourse.size_restrictions}
													onValuesChanged={(sizes) => {
														handleFieldUpdate({
															field: 'size_restrictions',
															targetValue: sizes
														})
													}}
													useChips={true}
													chipLabelRenderFunction={(value) => {
														return value.toUpperCase()
													}}
													menuItems={trainingSizes.map((size) => {
														return (
															<MenuItem key={`training-size-${size}`} value={size}>
																<Checkbox
																	style={{ textTransform: 'uppercase' }}
																	checked={
																		editedCourse.size_restrictions.indexOf(
																			size
																		) > -1
																	}
																/>
																{size.toUpperCase()}
															</MenuItem>
														)
													})}
												/>
											</div>
											<div>
												<StyledMultiSelect
													label="Vaccines"
													required
													disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
													values={editedCourse.required_vaccines}
													onValuesChanged={(vaxes) => {
														handleFieldUpdate({
															field: 'required_vaccines',
															targetValue: vaxes
														})
													}}
													useChips={true}
													chipLabelRenderFunction={(value = '') => {
														const vax = requiredVaccines.find((v) => v.id === value)
														return capitilizeMultipleWords(vax?.name || 'unknown')
													}}
													menuItems={requiredVaccines.map(({ id, name }) => {
														return (
															<MenuItem key={`training-vax-${id}`} value={id}>
																<Checkbox
																	checked={
																		editedCourse.required_vaccines.indexOf(
																			id
																		) > -1
																	}
																/>
																{name}
															</MenuItem>
														)
													})}
												/>
											</div>
										</div>
									</div>
								</div>
								<div>
									<h3 className={classes.rowHeader}>Course Pricing</h3>
									<div className={classes.rowWrapper}>
										<div className={classes.row}>
											<div>
												<StyledCurrencyTextField
													variant="filled"
													required
													label="Price per Session"
													initialValue={editedCourse.price_per_session}
													amountChanged={(e) => {
														handleFieldUpdate({
															field: 'price_per_session',
															type: 'number',
															targetValue: e.inCents
														})
													}}
												/>
											</div>
											<div>{NON_BREAKING_SPACE}</div>
										</div>
									</div>
								</div>
							</>
						) : null}
						{setupStep === SETUP_STEPS.SESSIONS ? (
							<>
								{editedCourse.sessions.map((session, index) => {
									const { session: sessionNumber, overview, training_materials = [] } = session
									return (
										<div>
											<h3
												className={
													classes.rowHeader
												}>{`Session Information (Week ${sessionNumber})`}</h3>
											<div className={classes.rowWrapper}>
												<div className={classes.row}>
													<div>
														<StyledTextField
															variant="filled"
															multiline
															charcounter
															required
															label="Session Overview (External)"
															disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
															value={overview}
															inputProps={{ maxLength: 350, minRows: 3 }}
															onChange={(e) => {
																const newSessions = [...editedCourse.sessions]
																newSessions[index] = {
																	...newSessions[index],
																	overview: e.target.value
																}
																handleFieldUpdate({
																	field: `sessions`,
																	type: 'object',
																	targetValue: newSessions
																})
															}}
														/>
													</div>
												</div>
												{training_materials.map((material, materialIndex) => {
													const { description, file_path, path_error } = material
													return (
														<>
															<div className={classes.row}>
																<div>
																	<StyledTextField
																		variant="filled"
																		multiline
																		charcounter
																		required
																		label="Homework Overview (External)"
																		disabled={
																			mode === DIALOG_EDIT_MODES.ViewOnly
																		}
																		value={description}
																		inputProps={{
																			maxLength: 1500,
																			minRows: 3
																		}}
																		onChange={(e) => {
																			const newSessions = [
																				...editedCourse.sessions
																			]
																			newSessions[index].training_materials[
																				materialIndex
																			] = {
																				...newSessions[index]
																					.training_materials[
																					materialIndex
																				],
																				description: e.target.value
																			}
																			handleFieldUpdate({
																				field: 'sessions',
																				type: 'object',
																				targetValue: newSessions
																			})
																		}}
																	/>
																</div>
															</div>
															<div className={classes.row}>
																<div>
																	<StyledTextField
																		variant="filled"
																		label="Homework Link (External)"
																		disabled={
																			mode === DIALOG_EDIT_MODES.ViewOnly
																		}
																		value={file_path}
																		required
																		inputProps={{ minRows: 3 }}
																		onChange={(e) => {
																			let isHTTP = true
																			if (e.target.value.length >= 8) {
																				// test value is a url with regex
																				isHTTP = /^(https:\/\/)/.test(
																					e.target.value
																				)
																			}
																			const newSessions = [
																				...editedCourse.sessions
																			]
																			newSessions[index].training_materials[
																				materialIndex
																			] = {
																				...newSessions[index]
																					.training_materials[
																					materialIndex
																				],
																				file_path: e.target.value,
																				path_error: isHTTP
																					? ''
																					: 'Invalid URL'
																			}
																			handleFieldUpdate({
																				field: 'sessions',
																				targetValue: newSessions
																			})
																		}}
																	/>
																</div>
																<div className={classes.alertContainer}>
																	{path_error > '' ? (
																		<StyledAlert
																			type="error"
																			text={path_error}
																			style={{ flex: 1 }}
																		/>
																	) : (
																		NON_BREAKING_SPACE
																	)}
																</div>
															</div>
														</>
													)
												})}
											</div>
										</div>
									)
								})}
							</>
						) : null}
						<div className={classes.dialogActions}>
							{setupStep === SETUP_STEPS.DETAILS ? (
								<MainButton
									label="Next"
									variant="contained"
									onClick={() => setSetupStep(SETUP_STEPS.SESSIONS)}
									disabled={editedCourse.number_of_sessions < 1}
								/>
							) : null}
							{setupStep === SETUP_STEPS.SESSIONS ? (
								<MainButton
									label={mode === DIALOG_EDIT_MODES.New ? 'SAVE' : 'UPDATE'}
									onClick={handleOnSave}
									variant="contained"
									disabled={!canSave}
								/>
							) : null}
							<MainButton label="Cancel" onClick={handleOnCancelled} />
						</div>
					</div>
				</DialogContent>
			</StyledDialog>
		</>
	)
}

export default TrainingCourseDialog
